import { useEffect, useRef, useState } from "react";
import {
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AnimatedTitle from "./Sidebar/AnimatedTitle";
import { motion } from "framer-motion";
import { isDarkMode } from "../../utils/constants";
import { useChatContext } from "../../contexts/ChatContext";
import { useAppContext } from "../../contexts/AppContext";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import UpgradeMaxModal from "./UpgradeMaxModal";
import {
  buildMaxUpgradeModalProps,
  maxUpgradePathFor,
} from "../../utils/max_upgrade_trial";

const MotionIconButton = motion(IconButton);
export default function SidebarThread({
  threads,
  renamingThreadUuid,
  renameInputRef,
  newTitle,
  setNewTitle,
  confirmRename,
  cancelRenaming,
  selectActiveThread,
  prefetchThreadMessages = () => {},
  handleOpenMenu,
  anchorEl,
  handleCloseMenu,
  startRenaming,
  handleDelete,
  preAccountUser,
  hasSubscription,
  canSwitchThreads,
  setIsSidebarOpen,
  scrollToThreadUuid = null,
  onThreadScrollHandled = () => {},
}) {
  const { activeThread, loadingResponse, loadingResponseByThreadUuid, unreadThreadUuids } = useChatContext();
  const { userData } = useAppContext();
  const threadNodeRefs = useRef({});
  const [showThreadSwitchModal, setShowThreadSwitchModal] = useState(false);
  const navigate = useNavigate();
  const threadSwitchUpgradeProps = buildMaxUpgradeModalProps(userData, {
    title: "Get more active chats with MAX.",
    description:
      "Your current chat is still answering. Upgrade to Max to have multiple active chats running and switch back and forth at any time.",
    footer: "Upgrade when you're ready for advanced multi-thread workflows.",
    trialTitle: "Try MAX free for 7 days.",
    trialDescription:
      "Start your 7-day MAX trial to run multiple active chats and switch while responses are still generating.",
    trialFooter:
      "Today is $0. Your Pro subscription stays active during the trial.",
  });

  useEffect(() => {
    if (!scrollToThreadUuid) return;
    const node = threadNodeRefs.current?.[scrollToThreadUuid];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    onThreadScrollHandled();
  }, [scrollToThreadUuid, threads, onThreadScrollHandled]);

  const setThreadNodeRef = (threadUuid) => (node) => {
    if (!threadUuid) return;
    if (node) {
      threadNodeRefs.current[threadUuid] = node;
    } else {
      delete threadNodeRefs.current[threadUuid];
    }
  };

  return (
    <>
      <Box sx={{ paddingX: 2, ml: 1 }}>
        <Typography variant="body2" sx={{ color: "#8E8E8E" }}>
          Chats
        </Typography>
      </Box>
      <List sx={{ paddingX: 2 }}>
        {preAccountUser || !threads?.length ? (
          <ListItemButton
            disabled={true}
            sx={{
              height: 46,
              mb: 1,
              borderRadius: 2,
              backgroundColor: isDarkMode ? "#272829" : "#D6D6D6",
            }}
          >
            <ListItemText
              primary={
                <AnimatedTitle
                  // key={`${t.threadUuid}-${t.title}`}
                  title={"User Chat"}
                />
              }
              sx={{ color: isDarkMode ? "white" : "black" }}
            />
          </ListItemButton>
        ) : (
          threads &&
          threads.map((t, i) => {
            const isActive = t.threadUuid === activeThread?.threadUuid;
            const isThreadSwitchBlocked =
              !isActive && !canSwitchThreads && Boolean(loadingResponse);
            const isTargeted = Boolean(t.isAdminTargeted);
            const showUnread =
              !isActive &&
              !t.threadUuid?.startsWith("temp-") &&
              Boolean(unreadThreadUuids?.[t.threadUuid]);
            const isLoading = Boolean(
              loadingResponseByThreadUuid?.[t.threadUuid],
            );

            if (renamingThreadUuid === t.threadUuid) {
              return (
                <Box
                  component="form"
                  key={t.threadUuid}
                  ref={setThreadNodeRef(t.threadUuid)}
                  noValidate
                  autoComplete="off"
                  sx={{
                    mb: 1,
                    pl: 2,
                    py: 0,
                    pr: 0,
                    borderRadius: 2,
                    backgroundColor: "#272829",
                    height: "46px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    fullWidth
                    value={newTitle}
                    inputRef={renameInputRef}
                    onChange={(e) => setNewTitle(e.target.value)}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        p: 0,
                        color: "white",
                        "& input": { p: 0 },
                      },
                    }}
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        confirmRename();
                      }
                    }}
                    onBlur={cancelRenaming}
                    sx={{
                      bgcolor: "#272829",
                      borderRadius: 2,
                      "& .MuiInputBase-input": { color: "white" },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Box>
              );
            }

            return (
              <ListItemButton
                key={t.threadUuid}
                ref={setThreadNodeRef(t.threadUuid)}
                data-chat-thread-uuid={t.threadUuid}
                onMouseEnter={() => {
                  if (!isActive) prefetchThreadMessages(t);
                }}
                onTouchStart={() => {
                  if (!isActive) prefetchThreadMessages(t);
                }}
                onFocus={() => {
                  if (!isActive) prefetchThreadMessages(t);
                }}
                onClick={() => {
                  if (isThreadSwitchBlocked) {
                    setShowThreadSwitchModal(true);
                    return;
                  }
                  selectActiveThread(t);
                  if (isMobile) {
                    // on mobile, automatically close the side panel
                    setTimeout(() => {
                      // setTimeout purely cosmetic here--it lets the threads load before showing
                      setIsSidebarOpen(false);
                    }, 250);
                  }
                }}
                sx={{
                  // height: 46,
                  mb: 1,
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: 2,
                  border: isTargeted
                    ? `1px solid ${isDarkMode ? "#F59E0B" : "#D97706"}`
                    : "1px solid transparent",
                  backgroundColor: isActive
                    ? isDarkMode
                      ? "#232A3D"
                      : "#D9E2F8"
                    : isTargeted
                      ? isDarkMode
                        ? "rgba(245,158,11,0.18)"
                        : "rgba(245,158,11,0.12)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: isDarkMode
                      ? isActive
                        ? "rgba(22,156,255,0.16)"
                        : isTargeted
                          ? "rgba(245,158,11,0.26)"
                          : "rgba(33, 41, 58, 0.7)"
                      : isTargeted
                        ? "rgba(245,158,11,0.2)"
                        : "rgba(217, 226, 248, 0.7)",
                    },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <AnimatedTitle
                        key={`${t.threadUuid}-${t.title}`}
                        title={t.title || `Thread ${i + 1}`}
                      />
                      {isLoading && !isActive && (
                        <CircularProgress
                          size={12}
                          thickness={6}
                          sx={{
                            color: isDarkMode ? "#93C5FD" : "#3B82F6",
                          }}
                        />
                      )}
                      {showUnread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#3B82F6",
                            boxShadow: isDarkMode
                              ? "0 0 0 2px rgba(59,130,246,0.25)"
                              : "0 0 0 2px rgba(59,130,246,0.15)",
                            flex: "0 0 auto",
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ color: isDarkMode ? "white" : "black" }}
                />
                {!t.threadUuid.startsWith("temp-") && (
                  <MotionIconButton
                    key={t.threadUuid}
                    edge="end"
                    onClick={(e) => handleOpenMenu(e, t)}
                    sx={{ color: isDarkMode ? "white" : "black" }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25 }}
                  >
                    <MoreHorizIcon />
                  </MotionIconButton>
                )}
              </ListItemButton>
            );
          })
        )}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#222" : "#E9E9E9",
            color: "white",
            "& .MuiMenuItem-root": {
              "&:hover": {
                backgroundColor: isDarkMode ? "#333333" : "#D6D6D6",
              },
              borderRadius: 2,
            },
          },
        }}
      >
        <MenuItem
          sx={{ color: isDarkMode ? "white" : "black" }}
          onClick={startRenaming}
        >
          Rename
        </MenuItem>
        <MenuItem sx={{ color: "#FF5252" }} onClick={handleDelete}>
          Delete
        </MenuItem>
      </Menu>

      <UpgradeMaxModal
        open={showThreadSwitchModal}
        onClose={() => setShowThreadSwitchModal(false)}
        onUpgrade={() => {
          setShowThreadSwitchModal(false);
          navigate(maxUpgradePathFor(userData));
        }}
        {...threadSwitchUpgradeProps}
      />
    </>
  );
}
